import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Switch,
  Tooltip,
  Button,
  Modal,
  TextField,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import SmsIcon from "@mui/icons-material/Sms";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { yellow, grey, blue } from "@mui/material/colors";
import axios from "axios";
import dayjs from "dayjs";
import cls from "./RdvAttendancePanel.module.css";
import RdvBulkInputModal from "../Modal/RdvBulkInput";
import {
  buildSmsLinkMessage,
  buildSupportAppLinkFromWorkplace,
  isSmsMessageTooLong,
  SMS_MSG_MAX_LEN,
} from "../../lib/rdvBulkInput/supportAppLink";

const switchstyle = {
  backgroundColor: grey[200],
  marginRight: 1,
  marginLeft: 1,
  borderRadius: 3,
};

const switchContainer = { // 스위치 컨테이너 스타일
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const statusTitle = { // 지원인력 상황판 헤더 스타일 (텍스트 줄바꿈 방지)
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontWeight: "bold",
  backgroundColor: yellow[800],
  color: "rgba(0,0,0,0.87)",
  padding: "0 8px",
  height: "30px",
  minHeight: "30px",
  maxHeight: "30px",
  boxSizing: "border-box",
  fontSize: "1.25rem",
  flexShrink: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
};

// 상황판처럼 세로선 제거, 가로선만 표시
const tblstyle = {
  fontWeight: "bold",
  backgroundColor: yellow[300],
  color: "rgba(0,0,0,0.87)",
  textAlign: "center",
  padding: "2px 2px",
  borderBottom: "1px solid #ccc",
};

const cellStyle = { // 셀 스타일
  textAlign: "center",
  padding: "2px 2px",
  borderBottom: "1px solid #ccc",
  backgroundColor: yellow[50],
  fontSize: "0.9rem",
  width: "auto",
};

const clickableCellStyle = { // 클릭 가능한 셀 스타일
  ...cellStyle,
  cursor: "pointer",
  "&:hover": { backgroundColor: yellow[200] },
};

const detailHeaderStyle = { // 세부 현황 헤더 스타일
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  fontWeight: "bold",
  backgroundColor: yellow[800],
  color: "rgba(0,0,0,0.87)",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 0.5,
  px: 0.5,
  py: 0.5,
  border: "1px solid",
  borderColor: yellow[700],
};

// 상황판 버튼과 동일한 라운드: style 기준 borderRadius 3 → 3px
const detailCloseBtn = {
  width: 35,
  height: 30,
  border: "1px solid",
  borderColor: "rgba(0,0,0,0.4)",
  borderRadius: "3px",
  cursor: "pointer",
  color: "rgba(0,0,0,0.87)",
  "&:hover": { backgroundColor: yellow[200], borderColor: "rgba(0,0,0,0.6)" },
  zIndex: 10,
  marginTop: "3px",
};

const detailExcelBtn = { // 세부 현황 엑셀 버튼 스타일
  width: 35,
  height: 30,
  border: "1px solid",
  borderColor: "rgba(0,0,0,0.4)",
  borderRadius: "3px",
  cursor: "pointer",
  color: "rgba(0,0,0,0.87)",
  "&:hover": { backgroundColor: yellow[200], borderColor: "rgba(0,0,0,0.6)" },
  zIndex: 10,
};

const rdvExcelBtn = {
  width: 30,
  height: 25,
  border: "solid 1px rgba(0,0,0,0.35)",
  borderRadius: 3,
  cursor: "pointer",
  zIndex: 10,
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

/** 동원자원 문자전송과 동일: 휴대번호 포맷 */
function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";
  const numbers = String(phoneNumber).replace(/\D/g, "");
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  return phoneNumber;
}

/** 재난관리 지원집결지와 동일: 원문자 1~10 ❶…❿, 11~20 ⓫…⓴, 그 이상 (n) */
function getCircledNum(n) {
  if (n >= 1 && n <= 10) return String.fromCodePoint(0x2776 + n - 1);
  if (n >= 11 && n <= 20) return String.fromCodePoint(0x24eb + n - 11);
  return `(${n})`;
}

const RDV_COLUMN_WIDTH = 70;  // 집결지 컬럼 너비
const RDV_NAME_MAX_LEN = 12;  // 집결지 컬럼 표시 최대 글자 수
/** 지원인력 상황판 테이블: 최대 표시 행 수, 초과분은 세로 스크롤 */
const RDV_TABLE_MAX_VISIBLE_ROWS = 5;  // 지원인력 상황판 테이블 최대 표시 행 수
const RDV_TABLE_ROW_HEIGHT_PX = 32;  // 지원인력 상황판 테이블 행 높이
const RDV_TABLE_HEADER_ROWS = 2;  // 지원인력 상황판 테이블 헤더 행 수
const rdvTableMaxHeightPx =  // 지원인력 상황판 테이블 최대 높이
  (RDV_TABLE_HEADER_ROWS + RDV_TABLE_MAX_VISIBLE_ROWS) * RDV_TABLE_ROW_HEIGHT_PX;   // 지원인력 상황판 테이블 최대 높이 계산

function parseDateYMD(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[0];
  const d = dayjs(s);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function getRecordDayIndex(record, disasterStartYmd) {
  const recordYmd = parseDateYMD(record?.check_in_dt || record?.ipdt);
  if (!recordYmd || !disasterStartYmd) return null;
  const diff = dayjs(recordYmd).diff(dayjs(disasterStartYmd), "day");
  if (diff < 0) return null;
  return diff + 1;
}

function isActiveAttendance(record) {
  const out = record?.check_out_dt;
  return out == null || String(out).trim() === "";
}

const dayFilterBtnSx = {
  flex: 1,
  py: 0.35,
  minHeight: 30,
  fontSize: "0.78rem",
  fontWeight: 700,
  textTransform: "none",
  color: "rgba(0,0,0,0.75)",
  borderColor: "rgba(0,0,0,0.22)",
  whiteSpace: "nowrap",
  "&.Mui-selected": {
    bgcolor: yellow[700],
    color: "#fff",
    borderColor: yellow[800],
    "&:hover": { bgcolor: yellow[800] },
  },
};

export default function RdvAttendancePanel(props) { // 지원인력 상황판 컴포넌트
  const { apiAddr, refreshBrd, fullswitch, requestOpenSupportPanel } = props;
  const [data, setData] = useState([]); // 지원인력 상황판 데이터
  const [loading, setLoading] = useState(true); // 지원인력 상황판 로딩 상태
  const [tab, setTab] = useState(0); // 0: 천제, 1: 소방, 2: 비소방
  const [dayFilter, setDayFilter] = useState("all"); // all | 1 | 2 | 3
  const [on, setOn] = useState(true); // On/Off 스위치
  const [detailOpen, setDetailOpen] = useState(false); // 세부 현황 판 열림 상태
  const [detailFilter, setDetailFilter] = useState(null); // { type: 'org'|'rdv', title: string, value } // 세부 현황 판 필터
  const [detailPanelTop, setDetailPanelTop] = useState(0);
  const [detailPanelLeft, setDetailPanelLeft] = useState(370); // 세부 현황 판 위치
  const [smsModalOpen, setSmsModalOpen] = useState(false); // 문자 전송 모달 열림 상태  
  const [smsModalItem, setSmsModalItem] = useState(null); // 문자 전송 모달 아이템
  const [smsPhoneInput, setSmsPhoneInput] = useState(""); // 문자 전송 모달 휴대번호 입력
  const [smsMessageInput, setSmsMessageInput] = useState(""); // 문자 전송 모달 메시지 입력
  const [smsReplyNumber, setSmsReplyNumber] = useState(() => // 문자 전송 모달 답변 번호
    typeof localStorage !== "undefined" && localStorage.rdvSmsReplyNumber
      ? localStorage.rdvSmsReplyNumber
      : "033-249-5664",
  ); // 문자 전송 모달 답변 번호 기본값
  const [smsSaveReplyNumber, setSmsSaveReplyNumber] = useState(false); // 문자 전송 모달 답변 번호 저장 상태
  const [smsSubmitting, setSmsSubmitting] = useState(false); // 문자 전송 모달 전송 상태
  const [showHelp, setShowHelp] = useState(false); // 도움말 표시 상태
  const [bulkInputOpen, setBulkInputOpen] = useState(false);
  const RDV_DETAIL_GAP_PX = 8; // 세부 현황 판 위치 간격

  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    const dsid =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.ondsid || "").trim()
        : "";
    if (!dsid || !apiAddr) {
      setData([]);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    const url = `${apiAddr}/get/rdvattendanceSummary?dsid=${encodeURIComponent(dsid)}&all=1`;
    try {
      const res = await axios.get(url, { timeout: 10000 });
      const list = Array.isArray(res.data) ? res.data : [];
      setData(list);
    } catch (e) {
      if (!silent) setData([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiAddr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const ondsid =
    typeof sessionStorage !== "undefined"
      ? (sessionStorage.ondsid || "").trim()
      : "";
  useEffect(() => {
    if (ondsid && apiAddr) fetchData();
  }, [ondsid, apiAddr, fetchData]);

  useEffect(() => {
    setDayFilter("all");
  }, [ondsid]);

  const disasterStartYmd = useMemo(
    () =>
      parseDateYMD(
        typeof sessionStorage !== "undefined" ? sessionStorage.onipdt : "",
      ),
    [ondsid],
  );

  const filteredData = useMemo(() => {
    if (dayFilter === "all") return data.filter(isActiveAttendance);
    const dayNum = Number(dayFilter);
    if (!disasterStartYmd || !dayNum) return data.filter(isActiveAttendance);
    return data.filter(
      (r) => getRecordDayIndex(r, disasterStartYmd) === dayNum,
    );
  }, [data, dayFilter, disasterStartYmd]);

  const dayCounts = useMemo(() => {
    const counts = {
      all: data.filter(isActiveAttendance).length,
      1: 0,
      2: 0,
      3: 0,
    };
    if (!disasterStartYmd) return counts;
    data.forEach((r) => {
      const day = getRecordDayIndex(r, disasterStartYmd);
      if (day >= 1 && day <= 3) counts[day] += 1;
    });
    return counts;
  }, [data, disasterStartYmd]);

  useEffect(() => {
    if (refreshBrd && apiAddr) fetchData({ silent: true });
  }, [refreshBrd, apiAddr, fetchData]);

  const prevRefreshBrd = useRef(null);
  useEffect(() => {
    if (fullswitch === false) {
      setOn(false);
      setDetailOpen(false);
    }
  }, [fullswitch]);

  // 지원인력 사인보드 클릭 시 지원인력 상황판 + 전체 지원인력현황 판 열기
  useEffect(() => {
    if (requestOpenSupportPanel) {
      setOn(true);
      setDetailFilter({ type: "all", title: "전체", value: null });
      setDetailOpen(true);
    }
  }, [requestOpenSupportPanel]);

  useEffect(() => {
    if (
      refreshBrd &&
      refreshBrd !== prevRefreshBrd.current &&
      !loading &&
      data.length >= 0
    ) {
      prevRefreshBrd.current = refreshBrd;
      const timer = setTimeout(() => {
        const tbody = document.querySelector("#rdvAttendanceTable tbody");
        if (tbody) {
          const cells = tbody.querySelectorAll("td");
          cells.forEach((td) => {
            td.classList.add(cls.yellowblink);
            setTimeout(() => td.classList.remove(cls.yellowblink), 3000);
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [refreshBrd, data, loading]);

  const {
    fireMatrix,
    otherMatrix,
    totalMatrix,
    rdvList,
    fireOrgs,
    otherOrgs,
    totalOrgs,
    totalPeople,
    firePeople,
    otherPeople,
  } = useMemo(() => {
    const rdvMap = {};
    filteredData.forEach((r) => {
      if (r.rdv_id != null && r.rname != null && !rdvMap[r.rdv_id])
        rdvMap[r.rdv_id] = r.rname;
    });
    const rdvList = Object.entries(rdvMap)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([id, rname]) => ({ rdv_id: Number(id), rname }));

    const fireData = filteredData.filter((r) => r.person_type === "fire");
    const otherData = filteredData.filter((r) => r.person_type === "other");
    const fireOrgsSet = [...new Set(fireData.map((r) => r.org || "기타"))];
    const otherOrgsSet = [
      ...new Set(otherData.map((r) => r.org || "비소속")),
    ];
    const totalOrgsSet = [...new Set([...fireOrgsSet, ...otherOrgsSet])];

    /** 소속 행을 원문자(열) 순서에 맞춰 정렬: 인원이 있는 가장 앞 열(원문자) 기준, 동일하면 소속명 가나다 */
    const sortOrgsByRdvOrder = (orgList, matrix) => {
      return [...orgList].sort((a, b) => {
        const minIdxA = rdvList.findIndex((r) => (matrix[a] || {})[r.rdv_id] > 0);
        const minIdxB = rdvList.findIndex((r) => (matrix[b] || {})[r.rdv_id] > 0);
        const idxA = minIdxA < 0 ? 9999 : minIdxA;
        const idxB = minIdxB < 0 ? 9999 : minIdxB;
        if (idxA !== idxB) return idxA - idxB;
        return String(a).localeCompare(String(b));
      });
    };

    const countMap = (arr, defaultOrg) => {
      const m = {};
      arr.forEach((r) => {
        const o = r.org || defaultOrg;
        const rv = r.rdv_id;
        if (!m[o]) m[o] = {};
        m[o][rv] = (m[o][rv] || 0) + 1;
      });
      return m;
    };
    const fireMatrix = countMap(fireData, "기타");
    const otherMatrix = countMap(otherData, "비소속");
    const totalMatrix = {};
    totalOrgsSet.forEach((org) => {
      totalMatrix[org] = {};
      rdvList.forEach((r) => {
        totalMatrix[org][r.rdv_id] =
          (fireMatrix[org]?.[r.rdv_id] || 0) +
          (otherMatrix[org]?.[r.rdv_id] || 0);
      });
    });

    const fireOrgs = sortOrgsByRdvOrder(fireOrgsSet, fireMatrix);
    const otherOrgs = sortOrgsByRdvOrder(otherOrgsSet, otherMatrix);
    const totalOrgs = sortOrgsByRdvOrder(totalOrgsSet, totalMatrix);

    const sumPeople = (matrix, orgs) =>
      orgs.reduce(
        (total, org) =>
          total +
          rdvList.reduce((s, r) => s + (matrix[org]?.[r.rdv_id] || 0), 0),
        0,
      );
    const totalPeople = sumPeople(totalMatrix, totalOrgs);
    const firePeople = sumPeople(fireMatrix, fireOrgs);
    const otherPeople = sumPeople(otherMatrix, otherOrgs);

    return {
      fireMatrix,
      otherMatrix,
      totalMatrix,
      rdvList,
      fireOrgs,
      otherOrgs,
      totalOrgs,
      totalPeople,
      firePeople,
      otherPeople,
    };
  }, [filteredData]);

  const detailList = useMemo(() => {
    if (!detailFilter || !filteredData.length) return [];
    let list;
    if (detailFilter.type === "all") {
      list = [...filteredData];
    } else if (detailFilter.type === "org") {
      list = filteredData.filter((r) => (r.org || "").trim() === detailFilter.value);
    } else if (detailFilter.type === "rdv") {
      list = filteredData.filter((r) => r.rdv_id === detailFilter.value);
    } else {
      list = [];
    }
    return list;
  }, [detailFilter, filteredData]);

  useEffect(() => {
    if (!detailOpen) return;
    const updatePosition = () => {
      const el = document.getElementById("rdvAttendancePanel");
      if (el) {
        const rect = el.getBoundingClientRect();
        const top = rect.bottom + RDV_DETAIL_GAP_PX;
        const left = rect.left;
        setDetailPanelTop(top);
        setDetailPanelLeft(left);
      }
    };
    const t = setTimeout(updatePosition, 50);
    const el = document.getElementById("rdvAttendancePanel");
    const statusPanelEl = document.getElementById("statusPanel");
    const cleanups = [];
    if (el && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(updatePosition);
      ro.observe(el);
      cleanups.push(() => ro.unobserve(el));
    }
    if (statusPanelEl && typeof ResizeObserver !== "undefined") {
      const roParent = new ResizeObserver(updatePosition);
      roParent.observe(statusPanelEl);
      cleanups.push(() => roParent.unobserve(statusPanelEl));
    }
    const statusBoardBoxEl = document.getElementById("statusBoardBox");
    if (statusBoardBoxEl && typeof ResizeObserver !== "undefined") {
      const roBoard = new ResizeObserver(updatePosition);
      roBoard.observe(statusBoardBoxEl);
      cleanups.push(() => roBoard.unobserve(statusBoardBoxEl));
    }
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    const intervalId = setInterval(updatePosition, 400);
    return () => {
      clearTimeout(t);
      clearInterval(intervalId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      cleanups.forEach((fn) => fn());
    };
  }, [detailOpen, detailFilter]);

  if (loading) {
    return (
      <Paper
        sx={{
          p: 2,
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Paper>
    );
  }

  const subTotalRowStyle = { // 소계 행 스타일 (높이 축소)
    ...cellStyle,
    fontWeight: "bold",
    backgroundColor: yellow[100],
  };

  const handleOrgClick = (org) => {
    setDetailFilter({ type: "org", title: org, value: org });
    setDetailOpen(true);
  };

  const handleRdvHeaderClick = (rdv) => {
    setDetailFilter({
      type: "rdv",
      title: rdv.rname || `집결지 ${rdv.rdv_id}`,
      value: rdv.rdv_id,
    });
    setDetailOpen(true);
  };

  const handleSubtotalLabelClick = () => {
    setDetailFilter({ type: "all", title: "전체", value: null });
    setDetailOpen(true);
  };

  const handleSubtotalCellClick = (rdv) => {
    setDetailFilter({
      type: "rdv",
      title: rdv.rname || `집결지 ${rdv.rdv_id}`,
      value: rdv.rdv_id,
    });
    setDetailOpen(true);
  };

  const detailTableToExcel = () => {
    try {
      if (detailList.length === 0) {
        alert("내보낼 데이터가 없습니다.");
        return;
      }
      const XLSX = require("xlsx");
      const table = document.getElementById("rdvDetailTable");
      if (!table) throw new Error("테이블을 찾을 수 없습니다");
      const data = Array.from(table.rows).map((row) =>
        Array.from(row.cells).map((cell) => cell.textContent.trim()),
      );
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, "지원인력현황.xlsx");
    } catch (e) {
      console.error(e);
      alert("엑셀 파일 생성 중 오류가 발생했습니다.");
    }
  };

  const handleOpenSmsModal = (r) => {
    setSmsModalItem(r);
    setSmsPhoneInput(formatPhoneNumber(r.pphone) || "");
    const dsid =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.ondsid || "").trim()
        : "";
    const dsnm =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.onname || "").trim()
        : "";
    const rdvId = r.rdv_id;
    const siteId =
      rdvId != null && Number(rdvId) <= 0 && Number(rdvId) !== 0
        ? Math.abs(Number(rdvId))
        : null;
    const link = buildSupportAppLinkFromWorkplace({
      dsid,
      dsnm,
      workplaceName: r.rname || "",
      rdvId: siteId ? null : rdvId,
      siteId,
      forSms: true,
      attSnum: r.snum,
    });
    setSmsMessageInput(buildSmsLinkMessage(link));
    setSmsModalOpen(true);
  };

  const handleSendSms = async () => {
    if (!smsModalItem || !apiAddr) return;
    const pnum = (smsPhoneInput || "").replace(/\D/g, "");
    if (!pnum || pnum.length < 10) {
      alert("올바른 휴대번호를 입력해주세요.");
      return;
    }
    const msg = (smsMessageInput || "").trim();
    if (!msg) {
      alert("보낼 문자 내용을 입력해주세요.");
      return;
    }
    if (isSmsMessageTooLong(msg)) {
      alert(
        `문자 내용이 ${SMS_MSG_MAX_LEN}자를 초과합니다. (${msg.length}자)\n동원자원앱 문자처럼 짧은 링크 형식을 사용하세요.`,
      );
      return;
    }
    const dsid =
      (typeof sessionStorage !== "undefined" && sessionStorage.ondsid) || "";
    if (!dsid) {
      alert("재난을 선택해주세요.");
      return;
    }
    setSmsSubmitting(true);
    try {
      const replyNum = (smsReplyNumber || "").trim() || "033-249-5664";
      const smsData = {
        pnum,
        callsgn: (
          smsModalItem.pname ||
          smsModalItem.insa_name ||
          "지원인력"
        ).trim(),
        msg,
        replyNumber: replyNum,
      };
      const res = await axios.post(
        `${apiAddr.replace(/\/$/, "")}/post/sendcustomsms`,
        smsData,
      );
      const data = res.data;
      if (data && data.result === "success") {
        if (smsSaveReplyNumber) {
          try {
            localStorage.rdvSmsReplyNumber = replyNum;
          } catch (e) {}
        }
        alert("문자가 발송되었습니다.");
        setSmsModalOpen(false);
        setSmsModalItem(null);
        setSmsMessageInput("");
      } else {
        alert(data?.message || "문자 발송에 실패했습니다.");
      }
    } catch (e) {
      console.warn("문자 발송 오류", e);
      alert(e.response?.data?.message || "문자 발송 중 오류가 발생했습니다.");
    } finally {
      setSmsSubmitting(false);
    }
  };

  const renderTable = (matrix, orgs) => {
    if (orgs.length === 0 && rdvList.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 2, textAlign: "center" }}
        >
          자료가 없습니다.
        </Typography>
      );
    }
    const subTotals = rdvList.map((r) =>
      orgs.reduce((s, org) => s + ((matrix[org] || {})[r.rdv_id] || 0), 0),
    );
    const subTotalSum = subTotals.reduce((a, b) => a + b, 0);
    const tableWidth = 100 + 110 + rdvList.length * RDV_COLUMN_WIDTH;
    const rdvHeaderCellSx = {
      ...tblstyle,
      width: RDV_COLUMN_WIDTH - 20,
      minWidth: RDV_COLUMN_WIDTH - 20,
      maxWidth: RDV_COLUMN_WIDTH - 20,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };
    const affiliationHeaderSx = { ...tblstyle, width: 88, minWidth: 88 };
    const sumColWidth = { width: 56, minWidth: 56, maxWidth: 56 };
    const sumHeaderSx = { ...tblstyle, ...sumColWidth };
    return (
      <TableContainer
        id="rdvAttendanceTable"
        sx={{
          width: tableWidth,
          minWidth: tableWidth,
          maxHeight: rdvTableMaxHeightPx,
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.05)" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.2)",
            borderRadius: 3,
          },
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ tableLayout: "fixed", width: tableWidth, minWidth: tableWidth }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={affiliationHeaderSx}>소속</TableCell>
              <TableCell sx={sumHeaderSx}>소계</TableCell>
              {rdvList.map((r, index) => {
                const fullName = r.rname || "(이름 없음)";
                const displayName =
                  fullName.length > RDV_NAME_MAX_LEN
                    ? fullName.slice(0, RDV_NAME_MAX_LEN) + "..."
                    : fullName;
                const label = `${getCircledNum(index + 1)} ${displayName}`;
                return (
                  <Tooltip
                    key={r.rdv_id}
                    title="클릭 시 세부 목록"
                    placement="top"
                    enterDelay={300}
                  >
                    <TableCell
                      sx={{
                        ...rdvHeaderCellSx,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: yellow[300] },
                      }}
                      onClick={() => handleRdvHeaderClick(r)}
                    >
                      {label}
                    </TableCell>
                  </Tooltip>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <Tooltip
                title="클릭 시 전체 세부 목록"
                placement="top"
                enterDelay={300}
              >
                <TableCell
                  sx={{
                    ...subTotalRowStyle,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: yellow[200] },
                  }}
                  onClick={handleSubtotalLabelClick}
                >
                  소계
                </TableCell>
              </Tooltip>
              <Tooltip
                title="클릭 시 전체 세부 목록"
                placement="top"
                enterDelay={300}
              >
                <TableCell
                  sx={{
                    ...subTotalRowStyle,
                    ...sumColWidth,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: yellow[200] },
                  }}
                  onClick={handleSubtotalLabelClick}
                >
                  {subTotalSum}
                </TableCell>
              </Tooltip>
              {rdvList.map((r, i) => (
                <Tooltip
                  key={r.rdv_id}
                  title={`클릭 시 ${r.rname || "집결지"} 세부 목록`}
                  placement="top"
                  enterDelay={300}
                >
                  <TableCell
                    sx={{
                      ...subTotalRowStyle,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: yellow[200] },
                    }}
                    onClick={() => handleSubtotalCellClick(r)}
                  >
                    {subTotals[i]}
                  </TableCell>
                </Tooltip>
              ))}
            </TableRow>
            {orgs.map((org) => {
              const row = matrix[org] || {};
              const total = rdvList.reduce(
                (s, r) => s + (row[r.rdv_id] || 0),
                0,
              );
              return (
                <TableRow key={org}>
                  <TableCell
                    sx={clickableCellStyle}
                    onClick={() => handleOrgClick(org)}
                  >
                    {org}
                  </TableCell>
                  <TableCell
                    sx={{ ...cellStyle, fontWeight: "bold", ...sumColWidth }}
                  >
                    {total}
                  </TableCell>
                  {rdvList.map((r) => (
                    <TableCell key={r.rdv_id} sx={cellStyle}>
                      {row[r.rdv_id] || 0}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const tableMinWidth = 100 + 100 + rdvList.length * RDV_COLUMN_WIDTH;
  const panelPadding = 16;
  // 헤더·탭 텍스트 줄바꿈 방지: '지원인력 상황판' + '전체 (0)' '소방 (0)' '비소방 (0)'이 들어갈 최소 너비
  const panelWidth = Math.max(320, tableMinWidth + panelPadding);

  return (
    <>
      <Paper
        id="rdvAttendancePanel"
        sx={{
          overflow: "hidden",
          width: on ? panelWidth : "auto",
          minWidth: on ? panelWidth : "unset",
          flexShrink: 0,
          alignSelf: "flex-start",
          border: "1px solid black",
          borderRadius: "10px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
        }}
      >
        <Box sx={{ ...statusTitle, gap: 1 }}>
          {on && (
            <LightTooltip title="지원인력 엑셀 일괄입력" placement="top" arrow>
              <UploadIcon
                sx={{
                  ...rdvExcelBtn,
                  "&:hover": { backgroundColor: yellow[200] },
                }}
                onClick={() => setBulkInputOpen(true)}
              />
            </LightTooltip>
          )}
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: "1.25rem", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            지원인력 상황판
          </Typography>
          <Box sx={{ marginLeft: "auto", ...switchContainer }}>
            <button
              onClick={() => setShowHelp(true)}
              title="사용 방법 안내"
              style={{
                width: 18, height: 18, borderRadius: "50%",
                border: "1.5px solid rgba(0,0,0,0.5)",
                backgroundColor: "transparent", color: "rgba(0,0,0,0.7)",
                fontSize: "0.7rem", fontWeight: "bold",
                cursor: "pointer", lineHeight: 1, padding: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                marginRight: 2,
              }}
            >?</button>
            <Switch
              size="small"
              checked={on}
              onChange={(e) => {
                const next = e.target.checked;
                setOn(next);
                if (!next) setDetailOpen(false);
              }}
              sx={{
                ...switchstyle,
                "& .MuiSwitch-thumb": {
                  backgroundColor: on ? blue[500] : grey[400],
                },
              }}
            />
          </Box>
        </Box>
        {on && (
          <>
            <Box
              sx={{
                px: 0.5,
                pt: 0.5,
                pb: 0.25,
                borderBottom: "1px solid rgba(0,0,0,0.1)",
                backgroundColor: yellow[100],
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={dayFilter}
                onChange={(_, v) => v != null && setDayFilter(v)}
              >
                <ToggleButton value="all" sx={dayFilterBtnSx}>
                  전체 ({dayCounts.all})
                </ToggleButton>
                <ToggleButton value="1" sx={dayFilterBtnSx} disabled={!disasterStartYmd}>
                  1일차 ({dayCounts[1]})
                </ToggleButton>
                <ToggleButton value="2" sx={dayFilterBtnSx} disabled={!disasterStartYmd}>
                  2일차 ({dayCounts[2]})
                </ToggleButton>
                <ToggleButton value="3" sx={dayFilterBtnSx} disabled={!disasterStartYmd}>
                  3일차 ({dayCounts[3]})
                </ToggleButton>
              </ToggleButtonGroup>
              {disasterStartYmd && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    px: 0.25,
                    color: "rgba(0,0,0,0.55)",
                    fontSize: "0.68rem",
                  }}
                >
                  출근일 기준 · 1일차={disasterStartYmd}
                  {dayFilter === "all"
                    ? " · 전체=현재 근무 중"
                    : " · 일차별=퇴근 포함"}
                </Typography>
              )}
            </Box>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 35,
                minWidth: 0,
                flexShrink: 0,
                "& .MuiTab-root": { minHeight: 35, py: 0.25, whiteSpace: "nowrap", fontSize: "0.9rem" },
              }}
            >
              <Tab label={`전체 (${totalPeople})`} />
              <Tab label={`소방 (${firePeople})`} />
              <Tab label={`비소방 (${otherPeople})`} />
            </Tabs>
            <Box sx={{ overflow: "visible", p: 0.5, minWidth: tableMinWidth, width: "100%", boxSizing: "border-box" }}>
              {tab === 0 && renderTable(totalMatrix, totalOrgs)}
              {tab === 1 && renderTable(fireMatrix, fireOrgs)}
              {tab === 2 && renderTable(otherMatrix, otherOrgs)}
            </Box>
          </>
        )}
      </Paper>

      {detailOpen && (
        <Box
          sx={{
            position: "fixed",
            zIndex: 200,
            top: detailPanelTop,
            left: detailPanelLeft,
            width: 850,
            maxWidth: "90vw",
            maxHeight: "60vh",
            backgroundColor: "white",
            border: "2px solid",
            borderColor: "grey.400",
            borderRadius: "10px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <Box sx={detailHeaderStyle}>
            <LightTooltip
              title="닫기"
              placement="top"
              arrow
              style={{ marginTop: "3px", marginLeft: "5px" }}
            >
              <CloseIcon
                sx={detailCloseBtn}
                onClick={() => setDetailOpen(false)}
              />
            </LightTooltip>
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", flex: 1, minWidth: 120 }}
            >
              {detailFilter?.title ?? ""} 지원인력 현황
            </Typography>
            <LightTooltip
              title="엑셀 저장"
              placement="top"
              arrow
              style={{ marginTop: "3px" }}
            >
              <DownloadIcon sx={detailExcelBtn} onClick={detailTableToExcel} />
            </LightTooltip>
            <LightTooltip title="지원인력 일괄입력" placement="top" arrow>
              <UploadIcon
                sx={{
                  ...detailExcelBtn,
                  ml: 0.5,
                }}
                onClick={() => setBulkInputOpen(true)}
              />
            </LightTooltip>
          </Box>
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table size="small" stickyHeader id="rdvDetailTable">
              <TableHead>
                <TableRow>
                  <TableCell sx={tblstyle}>근무지</TableCell>
                  <TableCell sx={tblstyle}>소속</TableCell>
                  <TableCell sx={tblstyle}>부서</TableCell>
                  <TableCell sx={tblstyle}>계급(직위)</TableCell>
                  <TableCell sx={tblstyle}>이름</TableCell>
                  <TableCell sx={tblstyle}>전화번호</TableCell>
                  <TableCell sx={tblstyle}>임무</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={cellStyle}>
                      해당 조건의 인원이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailList.map((r, i) => (
                    <TableRow key={r.snum ?? i}>
                      <TableCell sx={cellStyle}>
                        {r.rname ?? "-"}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {r.porg ?? r.porq ?? r.org ?? "-"}
                      </TableCell>
                      <TableCell sx={cellStyle}>{r.pdept ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>
                        {r.prank ?? r.insa_rank ?? "-"}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {r.person_type === "fire"
                          ? r.insa_name || r.pname || r.insa_code
                          : (r.pname ?? "-")}
                        {!isActiveAttendance(r) && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, color: "text.secondary" }}
                          >
                            (퇴근)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <span>
                            {formatPhoneNumber(r.pphone) || (r.pphone ?? "-")}
                          </span>
                          {r.pphone && (
                            <LightTooltip
                              title="문자 보내기"
                              placement="top"
                              arrow
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenSmsModal(r)}
                                sx={{
                                  minWidth: 0,
                                  py: 0.2,
                                  px: 0.5,
                                  minHeight: 0,
                                  lineHeight: 1.2,
                                }}
                              >
                                <SmsIcon sx={{ fontSize: "1rem" }} />
                              </Button>
                            </LightTooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellStyle}>{r.duty_desc ?? "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Modal
        open={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        aria-labelledby="rdv-sms-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 2,
          }}
        >
          <Typography
            id="rdv-sms-modal-title"
            variant="h6"
            color="gray"
            textAlign="center"
            mb={1}
          >
            문자전송 (지원인력)
          </Typography>
          {smsModalItem && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              수신:{" "}
              {smsModalItem.porg ?? smsModalItem.porq ?? smsModalItem.org ?? ""}{" "}
              /{" "}
              {smsModalItem.person_type === "fire"
                ? smsModalItem.insa_name || smsModalItem.pname
                : (smsModalItem.pname ?? "")}
            </Typography>
          )}
          <TextField
            fullWidth
            label="휴대번호"
            placeholder="010-1234-5678"
            value={smsPhoneInput}
            onChange={(e) => setSmsPhoneInput(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
            inputProps={{ maxLength: 13 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <TextField
              label="회신번호"
              placeholder="033-249-5664"
              value={smsReplyNumber}
              onChange={(e) => setSmsReplyNumber(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ maxLength: 20 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={smsSaveReplyNumber}
                  onChange={(e) => setSmsSaveReplyNumber(e.target.checked)}
                  size="small"
                />
              }
              label="저장"
              sx={{ flexShrink: 0 }}
            />
          </Box>
          <TextField
            fullWidth
            label="문자 내용"
            placeholder="지원앱 링크가 자동 입력됩니다"
            value={smsMessageInput}
            onChange={(e) => setSmsMessageInput(e.target.value)}
            size="small"
            multiline
            minRows={3}
            maxRows={6}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 2000 }}
          />
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSendSms}
              disabled={smsSubmitting}
            >
              {smsSubmitting ? "발송중..." : "발송"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSmsModalOpen(false)}
              disabled={smsSubmitting}
            >
              취소
            </Button>
          </Box>
        </Box>
      </Modal>
      {showHelp && ReactDOM.createPortal(
        <div
          onClick={() => setShowHelp(false)}
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            overflowY: "auto",
            padding: "65px 0 40px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: "28px 32px",
              maxWidth: 500,
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              position: "relative",
            }}
          >
            <button
              onClick={() => setShowHelp(false)}
              style={{
                position: "absolute", top: 12, right: 14,
                background: "none", border: "none",
                fontSize: "1.3rem", cursor: "pointer", color: "#888",
              }}
            >✕</button>
            <h3 style={{
              margin: "0 0 16px",
              color: "#2e7d32",
              fontSize: "1.05rem",
              borderBottom: "2px solid #a5d6a7",
              paddingBottom: 8,
            }}>📋 지원인력 상황판 사용 방법</h3>
            <ol style={{
              margin: 0,
              paddingLeft: 20,
              lineHeight: 2,
              fontSize: "0.9rem",
              color: "#333",
            }}>
              <li><b>전체</b>는 현재 근무 중(미퇴근) 인원, <b>1·2·3일차</b>는 해당일 출근 이력(퇴근 포함)입니다.</li>
              <li><b>전체 / 소방 / 비소방</b> 탭을 눌러 인력 유형별로 현황을 확인합니다.</li>
              <li><b>집결지 열</b>을 클릭하면 해당 집결지의 지원인력 상세 목록이 표시됩니다.</li>
              <li><b>기관 행</b>을 클릭하면 해당 기관의 인력 상세 목록이 표시됩니다.</li>
              <li>상세 목록에서 인원별 <b>문자</b> 버튼으로 지원앱 출근 링크 SMS를 발송할 수 있습니다.</li>
              <li>상단 <b>업로드(↑)</b> 버튼으로 엑셀 일괄입력·링크 문자 일괄 발송을 할 수 있습니다.</li>
              <li>엑셀 다운로드 버튼으로 현재 상세 목록을 <b>Excel 파일</b>로 저장합니다.</li>
            </ol>
            <hr style={{ margin: "16px 0", borderColor: "#e0e0e0" }} />
            <h3 style={{
              margin: "0 0 16px",
              color: "#1565c0",
              fontSize: "1.05rem",
              borderBottom: "2px solid #90caf9",
              paddingBottom: 8,
            }}>📱 QR 출퇴근 입력 방법</h3>
            <p style={{ margin: "0 0 8px", fontSize: "0.88rem", color: "#555", lineHeight: 1.6 }}>
              재난현장 관리 화면에서 집결지 행의 <b>QR 아이콘</b>을 클릭합니다.
            </p>
            <div style={{ textAlign: "center", margin: "10px 0 14px" }}>
              <img
                src="/help-images/qr-button.png"
                alt="QR 버튼 위치"
                style={{
                  maxWidth: "100%",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              />
              <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#888" }}>
                ▲ 집결지 행의 QR 아이콘 (출퇴근 QR) 클릭
              </p>
            </div>
            <p style={{ margin: "0 0 8px", fontSize: "0.88rem", color: "#555", lineHeight: 1.6 }}>
              팝업에서 <b>재난명·근무지가 포함된 QR코드</b>를 확인 후 출력하거나, 지원인력이 직접 스캔합니다.
            </p>
            <div style={{ textAlign: "center", margin: "10px 0 14px" }}>
              <img
                src="/help-images/qr-modal.png"
                alt="QR 출퇴근 관리 모달"
                style={{
                  maxWidth: "80%",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              />
              <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#888" }}>
                ▲ QR코드에 재난·자원집결지 정보 포함 — 스캔 시 출퇴근 자동 기록
              </p>
            </div>
            <ol style={{
              margin: "4px 0 0",
              paddingLeft: 20,
              lineHeight: 2,
              fontSize: "0.9rem",
              color: "#333",
            }}>
              <li>지원인력이 집결지에서 <b>QR 코드를 스캔</b>하면 출근이 자동 기록됩니다.</li>
              <li>퇴근 스캔 시 해당 인원의 상태가 <b>퇴근</b>으로 업데이트됩니다.</li>
              <li><b>인쇄미리보기</b>를 통해 QR코드를 인쇄하여 현장에 부착할 수 있습니다.</li>
            </ol>
            <hr style={{ margin: "16px 0", borderColor: "#e0e0e0" }} />
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#888", lineHeight: 1.9 }}>
              💡 <b>[스위치]</b> — 상황판 표시/숨기기 &nbsp;|&nbsp;
              📊 <b>[엑셀]</b> — 상세 목록 다운로드 &nbsp;|&nbsp;
              ✉️ <b>[문자]</b> — 선택 인원에게 SMS 발송
            </p>
          </div>
        </div>,
        document.body
      )}

      <RdvBulkInputModal
        apiAddr={apiAddr}
        open={bulkInputOpen}
        onClose={() => setBulkInputOpen(false)}
        onSaved={() => fetchData({ silent: true })}
      />
    </>
  );
}
